/**
 * Stool generator — a parametric stool covering kitchen, counter, and bar heights.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width, Y = depth, Z = height
 *
 * Local anchor: origin at the geometric centre of the footprint at Z = 0.
 *
 * Seat shapes:
 *   round  — cylindrical seat (24-segment cylinder).
 *   square — box seat.
 *
 * Leg styles:
 *   four_leg  — four corner legs, inset from the seat edge.
 *   pedestal  — central column with a flat square base.
 *   tripod    — three legs at 120° intervals, leaning outward.
 *
 * Optional footrest: a horizontal ring / bar at a fixed height for bar and
 * counter stools. Implemented as a single rectangular rail connecting pairs
 * of opposite legs (or a square loop for four_leg) at `footrestHeight`.
 *
 * Optional back: a simple vertical panel or bar rising above the seat at the
 * rear, for those stools that have one. Identical in spirit to the chair back.
 *
 * Height presets:
 *   kitchen  — 0.60 m   (standard kitchen / dining seat height)
 *   counter  — 0.65 m   (slightly taller counter stool)
 *   bar      — 0.75 m   (bar stool)
 *   custom   — user-defined
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import {
  buildBox,
  buildCylinder,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface StoolParams extends GeneratorParams {
  heightPreset: string;
  seatHeight: number;
  seatDiameter: number;
  seatThickness: number;
  seatShape: string;
  legStyle: string;
  legThickness: number;
  hasFootrest: boolean;
  footrestHeight: number;
  hasBack: boolean;
  backHeight: number;
  backThickness: number;
}

const STOOL_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "stool-seat",
    label: "Seat",
    params: [
      {
        id: "heightPreset",
        label: "Height Preset",
        type: "enum",
        default: "bar",
        options: [
          { value: "kitchen", label: "Kitchen (0.60 m)" },
          { value: "counter", label: "Counter (0.65 m)" },
          { value: "bar", label: "Bar (0.75 m)" },
          { value: "custom", label: "Custom" },
        ],
      },
      {
        id: "seatHeight",
        label: "Seat Height",
        type: "number",
        unit: "meters",
        default: 0.75,
        min: 0.4,
        max: 1.1,
        step: 0.01,
        showIf: { id: "heightPreset", value: "custom" },
      },
      {
        id: "seatDiameter",
        label: "Diameter",
        type: "number",
        unit: "meters",
        default: 0.36,
        min: 0.2,
        max: 0.7,
        step: 0.01,
      },
      {
        id: "seatThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.02,
        max: 0.15,
        step: 0.005,
      },
      {
        id: "seatShape",
        label: "Shape",
        type: "enum",
        default: "round",
        options: [
          { value: "round", label: "Round" },
          { value: "square", label: "Square" },
        ],
      },
    ],
  },
  {
    type: "section",
    id: "stool-legs",
    label: "Legs",
    params: [
      {
        id: "legStyle",
        label: "Style",
        type: "enum",
        default: "four_leg",
        options: [
          { value: "four_leg", label: "Four Legs" },
          { value: "pedestal", label: "Pedestal" },
          { value: "tripod", label: "Tripod" },
        ],
      },
      {
        id: "legThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.03,
        min: 0.015,
        max: 0.08,
        step: 0.005,
      },
      {
        id: "hasFootrest",
        label: "Footrest",
        type: "boolean",
        default: true,
        showIf: { id: "legStyle", value: ["four_leg", "tripod"] },
      },
      {
        id: "footrestHeight",
        label: "Footrest Height",
        type: "number",
        unit: "meters",
        default: 0.28,
        min: 0.1,
        max: 0.6,
        step: 0.01,
        showIf: { id: "hasFootrest", value: true },
      },
    ],
  },
  {
    type: "section",
    id: "stool-back",
    label: "Back",
    defaultCollapsed: true,
    params: [
      { id: "hasBack", label: "Back", type: "boolean", default: false },
      {
        id: "backHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.35,
        min: 0.1,
        max: 0.8,
        step: 0.025,
        showIf: { id: "hasBack", value: true },
      },
      {
        id: "backThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.025,
        min: 0.01,
        max: 0.08,
        step: 0.005,
        showIf: { id: "hasBack", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Height presets
// ---------------------------------------------------------------------------

const HEIGHT_PRESETS: Record<string, number> = {
  kitchen: 0.6,
  counter: 0.65,
  bar: 0.75,
};

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Identity local transform at a given world position. */
function lt(pos: [number, number, number]) {
  return {
    position: pos,
    rotation: [0, 0, 0] as [number, number, number],
    scale: [1, 1, 1] as [number, number, number],
  };
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildStool(
  p: StoolParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const preset = enumParam(p, "heightPreset", "bar");
  const seatH =
    preset === "custom"
      ? numParam(p, "seatHeight", 0.75)
      : (HEIGHT_PRESETS[preset] ?? 0.75);
  const seatD = numParam(p, "seatDiameter", 0.36);
  const seatT = numParam(p, "seatThickness", 0.05);
  const seatShape = enumParam(p, "seatShape", "round");
  const legStyle = enumParam(p, "legStyle", "four_leg");
  const legT = numParam(p, "legThickness", 0.03);
  const hasFootrest = boolParam(p, "hasFootrest", true);
  const footH = numParam(p, "footrestHeight", 0.28);
  const hasBack = boolParam(p, "hasBack", false);
  const backH = numParam(p, "backHeight", 0.35);
  const backT = numParam(p, "backThickness", 0.025);

  const children: GeneratorChild[] = [];

  // ── Seat ──────────────────────────────────────────────────────────────────
  // Centre of seat cushion sits at Z = seatH - seatT/2.
  const seatGeo: PrimitiveGeometry =
    seatShape === "round"
      ? buildCylinder(seatD * 0.5, seatT, 24)
      : buildBox(seatD, seatD, seatT);

  children.push({
    roleId: "seat",
    name: "Seat",
    objectKind: "imported",
    geometry: seatGeo,
    localTransform: lt([0, 0, seatH - seatT * 0.5]),
  });

  // ── Legs ──────────────────────────────────────────────────────────────────
  const legH = seatH - seatT;
  const legCZ = legH * 0.5;
  const legR = seatD * 0.5 * 0.75; // legs spread to 75 % of seat radius

  if (legStyle === "pedestal") {
    // Central column + wide base plate
    const colH = legH;
    const baseH = Math.max(0.03, legT);
    const baseW = Math.max(legT * 2, seatD * 0.45);
    const colGeo = concatPrimitiveGeometries([
      transformGeometry(buildCylinder(legT, colH - baseH), [
        0,
        0,
        baseH + (colH - baseH) * 0.5,
      ]),
      transformGeometry(buildBox(baseW, baseW, baseH), [0, 0, baseH * 0.5]),
    ]);
    children.push({
      roleId: "pedestal",
      name: "Pedestal",
      objectKind: "imported",
      geometry: colGeo,
      localTransform: lt([0, 0, 0]),
    });
  } else if (legStyle === "tripod") {
    // Three legs meeting at centre-top (0, 0, legH), splaying to floor at
    // radius legR. Each leg is a cylinder oriented from its floor point to the
    // shared apex.
    //
    // Rotation decomposition: rotating +Z to the (floor→apex) unit vector
    // whose tilt axis lies in the XY plane at azimuth `angle` uses:
    //   R = Rz(angle) · Ry(−tiltAngle) · Rz(−angle)
    // Applied as three chained transformGeometry calls, then positioned at
    // the midpoint between apex and floor point.
    const tiltAngle = Math.atan2(legR, legH);
    const len = Math.hypot(legR, legH);

    for (let i = 0; i < 3; i++) {
      const angle = (i / 3) * 2 * Math.PI;

      let geo = buildCylinder(legT * 0.5, len, 8);
      geo = transformGeometry(geo, [0, 0, 0], [0, 0, -angle]);
      geo = transformGeometry(geo, [0, 0, 0], [0, -tiltAngle, 0]);
      geo = transformGeometry(geo, [0, 0, 0], [0, 0, angle]);
      // Midpoint between apex (0, 0, legH) and floor (cos·legR, sin·legR, 0)
      geo = transformGeometry(geo, [
        Math.cos(angle) * legR * 0.5,
        Math.sin(angle) * legR * 0.5,
        legH * 0.5,
      ]);

      children.push({
        roleId: `leg_${i}`,
        name: `Leg ${i + 1}`,
        objectKind: "imported",
        geometry: geo,
        localTransform: lt([0, 0, 0]),
      });
    }

    // Footrest — three rungs connecting adjacent legs at height footH.
    // At that height each leg is at radius r = (1 − footH/legH) · legR from centre.
    if (hasFootrest && footH < legH * 0.9) {
      const r = (1 - footH / legH) * legR;
      const rT = legT * 0.7;
      for (let i = 0; i < 3; i++) {
        const a0 = (i / 3) * 2 * Math.PI;
        const a1 = ((i + 1) / 3) * 2 * Math.PI;
        const x0 = Math.cos(a0) * r,
          y0 = Math.sin(a0) * r;
        const x1 = Math.cos(a1) * r,
          y1 = Math.sin(a1) * r;
        const rungLen = Math.hypot(x1 - x0, y1 - y0);
        const rungAngle = Math.atan2(y1 - y0, x1 - x0);
        const geo = transformGeometry(
          buildBox(rungLen, rT, rT),
          [(x0 + x1) * 0.5, (y0 + y1) * 0.5, footH],
          [0, 0, rungAngle],
        );
        children.push({
          roleId: `footrest_${i}`,
          name: `Footrest ${i + 1}`,
          objectKind: "imported",
          geometry: geo,
          localTransform: lt([0, 0, 0]),
        });
      }
    }
  } else {
    // Four-leg — legs at 45° diagonals from the centre, at legR spread.
    const spread = legR * Math.cos(Math.PI / 4);
    const legGeo = buildBox(legT, legT, legH);
    for (let i = 0; i < 4; i++) {
      const angle = Math.PI * 0.25 + i * Math.PI * 0.5;
      const lx = Math.cos(angle) * spread;
      const ly = Math.sin(angle) * spread;
      children.push({
        roleId: `leg_${i}`,
        name: `Leg ${i + 1}`,
        objectKind: "imported",
        geometry: legGeo,
        localTransform: lt([lx, ly, legCZ]),
      });
    }

    // Footrest — four rungs connecting each adjacent pair of legs.
    // Legs are at (±spread, ±spread) so adjacent rungs are axis-aligned;
    // chord midpoints and lengths computed from actual leg positions.
    if (hasFootrest && footH < legH * 0.9) {
      const rT = legT * 0.7;
      for (let i = 0; i < 4; i++) {
        const a0 = Math.PI * 0.25 + i * Math.PI * 0.5;
        const a1 = Math.PI * 0.25 + (i + 1) * Math.PI * 0.5;
        const x0 = Math.cos(a0) * spread,
          y0 = Math.sin(a0) * spread;
        const x1 = Math.cos(a1) * spread,
          y1 = Math.sin(a1) * spread;
        const rungLen = Math.max(0.01, Math.hypot(x1 - x0, y1 - y0) - legT);
        const rungAngle = Math.atan2(y1 - y0, x1 - x0);
        const geo = transformGeometry(
          buildBox(rungLen, rT, rT),
          [(x0 + x1) * 0.5, (y0 + y1) * 0.5, footH],
          [0, 0, rungAngle],
        );
        children.push({
          roleId: `footrest_${i}`,
          name: `Footrest ${i + 1}`,
          objectKind: "imported",
          geometry: geo,
          localTransform: lt([0, 0, 0]),
        });
      }
    }
  }

  // ── Back ──────────────────────────────────────────────────────────────────
  if (hasBack) {
    // Single flat panel rising above the rear of the seat.
    const panelGeo = buildBox(seatD * 0.8, backT, backH);
    const hingeY = -(seatD * 0.5 * 0.4 + backT * 0.5);
    const raised = transformGeometry(panelGeo, [0, 0, backH * 0.5]);
    const placed = transformGeometry(raised, [0, hingeY, seatH]);
    children.push({
      roleId: "back",
      name: "Back",
      objectKind: "imported",
      geometry: placed,
      localTransform: lt([0, 0, 0]),
    });
  }

  return {
    kind: "group",
    parent: { name: "Stool" },
    children,
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const STOOL_GENERATOR: Generator = defineGenerator<StoolParams>({
  id: "stool",
  label: "Stool",
  category: "prop",
  paramSpecs: STOOL_SPECS,
  defaultParams: paramsFromSpecs(STOOL_SPECS) as StoolParams,
  build: buildStool,
});
